import moment from "moment";
import { useState } from "react";
import { TbTarget } from "react-icons/tb";
import { GetProfileOverviewQueryQuery } from "types/graphql-schema";

import getSidebarDataQuery from "@apps/main/graphql/get-sidebar-data-query";
import MeetingDialog from "@apps/meeting-dialog/meeting-dialog";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar } from "@cache/cache";
import Button, { buttonTheme } from "@components/button/button";
import GoalStatusPill from "@components/goal-status-pill/goal-status-pill";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import {
  assertEdgesNonNull,
  getUrl,
  removeCurrentYear,
} from "@helpers/helpers";

const ProfileSidebarHeader = ({
  data,
}: {
  data: NonNullable<GetProfileOverviewQueryQuery>;
}) => {
  const label = useLabel();
  const organization = currentOrganizationVar();
  const [showCreateMeetingModal, setShowCreateMeetingModal] = useState(false);

  const handleClickCreateMeeting = () => {
    setShowCreateMeetingModal(true);
  };

  const profile = data?.user;
  const meetings = data?.user?.nextOneonones
    ? assertEdgesNonNull(data.user.nextOneonones)
    : [];
  const ownedGoals = (
    data?.user?.ownedGoals ? assertEdgesNonNull(data.user.ownedGoals) : []
  ).filter((goal) => goal.__typename === "GoalArtifactNode");
  const next1on1 = meetings.at(0);
  const maxGoalsToShow = 5;

  if (!profile) {
    return null;
  }

  return (
    <>
      {/* Next 1-on-1 + goals preview */}
      <div className="flex items-center gap-4">
        {next1on1 &&
        next1on1.meetingGroupId &&
        organization.featureFlags.meetings ? (
          <Button
            theme={buttonTheme.lightGray}
            to={getUrl({
              meetingGroupId: next1on1.meetingGroupId,
              meetingId: next1on1.id,
            })}
          >
            <TopicflowIcon type="meeting" className="w-4 h-4" />
            Next {label("1-on-1")}:{" "}
            {removeCurrentYear(
              moment(next1on1.startDatetime).format("MMM Do, YYYY HH:mm")
            )}
          </Button>
        ) : organization.featureFlags.meetings ? (
          <Button
            theme={buttonTheme.lightGray}
            onClick={handleClickCreateMeeting}
          >
            <TopicflowIcon type="meeting" className="w-4 h-4" />
            No scheduled {label("1-on-1")}
          </Button>
        ) : null}
        {organization.featureFlags.goals && showCreateMeetingModal && (
          <MeetingDialog
            formOptions={{
              attendees: [profile],
              isFormalOneonone: true,
            }}
            refetchQueries={[getSidebarDataQuery]}
            onSaved={() => {
              setShowCreateMeetingModal(false);
            }}
            onClose={() => setShowCreateMeetingModal(false)}
          />
        )}
        {organization.featureFlags.goals && (
          <Button
            theme={buttonTheme.lightGray}
            to={`/goals/user/${profile.id}`}
          >
            <TbTarget className="w-4 h-4" />
            {label("goal", { pluralize: true, titleCase: true })}:
            <span className="flex gap-1 items-center">
              {ownedGoals.length === 0 && "0"}
              {ownedGoals.slice(0, maxGoalsToShow).map((goal) => (
                <GoalStatusPill
                  key={goal.id}
                  status={goal.status}
                  state={goal.state}
                  isStale={goal.isStale}
                />
              ))}
              {ownedGoals.length > maxGoalsToShow && (
                <span className="text-gray-500 text-sm">
                  +{ownedGoals.length - maxGoalsToShow}
                </span>
              )}
            </span>
          </Button>
        )}
      </div>
    </>
  );
};

export default ProfileSidebarHeader;
